import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closeAll = () => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  const isLight = !scrolled && !mobileMenuOpen;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileMenuOpen
          ? "bg-background-50 border-b border-background-200/70"
          : "bg-gradient-to-b from-black/40 to-transparent"
      }`}
    >
      <div className="flex items-center justify-between h-16 px-4 md:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 flex-shrink-0" onClick={closeAll}>
          <div className="w-8 h-8 flex items-center justify-center bg-accent-500 rounded-lg">
            <i className="ri-file-list-3-line text-background-50 text-base"></i>
          </div>
          <span className={`font-heading text-lg font-semibold hidden sm:block ${isLight ? "text-background-50" : "text-foreground-950"}`}>
            CSSBuy Spreadsheet
          </span>
        </Link>

        {/* Desktop Nav Links */}
        <div className="hidden md:flex items-center gap-2">
          <Link
            to="/"
            className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              isLight
                ? "text-background-50/90 hover:bg-background-50/15"
                : "text-foreground-700 hover:bg-background-200"
            }`}
          >
            <i className="ri-home-line text-base"></i>
            Início
          </Link>

          <Link
            to="/cssbuy-guia-completo"
            className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              isLight
                ? "text-background-50/90 hover:bg-background-50/15"
                : "text-foreground-700 hover:bg-background-200"
            }`}
          >
            <i className="ri-book-open-line text-base"></i>
            Guia de Compras
          </Link>

          {/* Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${
                isLight
                  ? "border-background-50/30 text-background-50 hover:bg-background-50/15"
                  : "border-foreground-200 bg-background-50 text-foreground-800 hover:border-foreground-400"
              }`}
            >
              <i className="ri-grid-line text-base"></i>
              <span>Categorias</span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${dropdownOpen ? "rotate-180" : ""}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-[560px] max-w-[90vw] bg-foreground-900 rounded-xl shadow-lg overflow-hidden z-50">
                <div className="p-4">
                  <p className="text-background-50/70 text-xs font-medium uppercase tracking-wider mb-3 px-1">
                    Todas as Categorias
                  </p>
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-background-50/90 hover:bg-foreground-800 transition-colors text-sm whitespace-nowrap"
                      >
                        <div className="w-7 h-7 flex items-center justify-center rounded-md bg-foreground-800 flex-shrink-0">
                          <i className="ri-arrow-right-up-line text-background-50/60 text-xs"></i>
                        </div>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
                <div className="border-t border-foreground-700 px-4 py-3">
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-accent-500 text-background-50 font-medium text-sm hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Ver Todas no Site Principal
                    <i className="ri-external-link-line text-sm"></i>
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* CTA Buttons */}
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            CSSBuy Spreadsheet
            <i className="ri-arrow-right-up-line text-sm"></i>
          </a>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full border-2 text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${
              isLight
                ? "border-background-50 text-background-50 hover:bg-background-50/15"
                : "border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-background-50"
            }`}
          >
            Comprar Agora
            <i className="ri-shopping-cart-line text-sm"></i>
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`md:hidden flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
            isLight && !mobileMenuOpen ? "text-background-50" : "text-foreground-800"
          }`}
        >
          <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 max-h-[70vh] overflow-y-auto">
          {/* Mobile Nav Links */}
          <div className="space-y-1 mb-4">
            <Link
              to="/"
              onClick={closeAll}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-800 hover:bg-background-100 transition-colors text-sm font-medium"
            >
              <i className="ri-home-line text-lg"></i>
              Início
            </Link>
            <Link
              to="/cssbuy-guia-completo"
              onClick={closeAll}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-800 hover:bg-background-100 transition-colors text-sm font-medium"
            >
              <i className="ri-book-open-line text-lg"></i>
              Guia de Compras
            </Link>
          </div>

          {/* Mobile Categories */}
          <p className="text-xs text-foreground-400 font-medium uppercase tracking-wider px-3 mb-3">
            Categorias da Planilha
          </p>
          <div className="grid grid-cols-2 gap-1 mb-4">
            {categories.map((cat, idx) => {
              const catIcons: Record<string, string> = {
                shoes: "ri-footprint-line",
                hoodies: "ri-t-shirt-line",
                tshirts: "ri-shirt-line",
                jackets: "ri-shirt-line",
                pants: "ri-check-line",
                headwear: "ri-sun-line",
                sets: "ri-stack-line",
                underwear: "ri-hand-heart-line",
                jersey: "ri-basketball-line",
                accessories: "ri-vip-diamond-line",
                bags: "ri-handbag-line",
                belts: "ri-link",
                perfumes: "ri-drop-line",
                other: "ri-more-line",
              };
              return (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeAll}
                  className="flex items-center gap-2 px-2.5 py-2.5 rounded-lg text-foreground-700 hover:bg-background-100 transition-colors text-xs font-medium"
                >
                  <div className="w-7 h-7 flex items-center justify-center rounded-md bg-background-200/70 flex-shrink-0">
                    <i className={`${catIcons[cat.id] || "ri-price-tag-3-line"} text-foreground-500 text-sm`}></i>
                  </div>
                  {cat.name}
                </a>
              );
            })}
          </div>

          {/* Mobile CTA */}
          <div className="space-y-2 pt-3 border-t border-background-200/70">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeAll}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm cursor-pointer hover:bg-primary-600 transition-colors"
            >
              CSSBuy Spreadsheet
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeAll}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-full border-2 border-primary-500 text-primary-500 font-semibold text-sm cursor-pointer hover:bg-primary-500 hover:text-background-50 transition-colors"
            >
              Comprar Agora
              <i className="ri-shopping-cart-line text-sm"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}